import { Result } from '@blade/std-lib'
import { loadScript } from '@root/shared/core/DomUtil'
import { LoadScriptError } from '@root/shared/core/script-loader/script-loader'

declare global {
  interface Window {
    PinUtils?: PinterestPinUtils
  }
}

export interface PinterestPinUtils {
  build: (node?: HTMLElement) => void
}

const pinterestWidgetJs = 'https://assets.pinterest.com/js/pinit.js'
const pinUtilsWaitTimeoutMs = 10_000
const pinUtilsPollIntervalMs = 50

let pinterestWidgetPromise:
  | Promise<Result.Result<PinterestPinUtils, LoadScriptError>>
  | undefined

const waitForPinUtils = (): Promise<
  Result.Result<PinterestPinUtils, LoadScriptError>
> =>
  new Promise((resolve) => {
    const startedAt = Date.now()

    const poll = () => {
      if (window.PinUtils?.build) {
        resolve(Result.succeed(window.PinUtils))

        return
      }

      if (Date.now() - startedAt >= pinUtilsWaitTimeoutMs) {
        resolve(
          Result.fail(
            new LoadScriptError({
              src: pinterestWidgetJs,
              cause: 'window.PinUtils is undefined',
            }),
          ),
        )

        return
      }

      window.setTimeout(poll, pinUtilsPollIntervalMs)
    }

    poll()
  })

export const loadPinterestWidget = () => {
  if (pinterestWidgetPromise) {
    return pinterestWidgetPromise
  }

  pinterestWidgetPromise = new Promise<
    Result.Result<PinterestPinUtils, LoadScriptError>
  >((resolve) => {
    loadScript(pinterestWidgetJs, {
      async: true,
    }).then(async (result) => {
      if (Result.isFailure(result)) {
        pinterestWidgetPromise = undefined
        resolve(Result.fail(result.failure))

        return
      }

      const pinUtilsResult = await waitForPinUtils()

      if (Result.isFailure(pinUtilsResult)) {
        pinterestWidgetPromise = undefined
      }

      resolve(pinUtilsResult)
    })
  })

  return pinterestWidgetPromise
}
